import { useState } from "react";
import { Modal } from "./components/ui";
import { Icon } from "./components/Icons";
import { useStore } from "./lib/store";
import { usePmStore } from "./lib/pmStore";
import { accessActions } from "./lib/accessStore";
import { PERMISSIONS, LEVEL_LABEL } from "./lib/permissions";
import type { AccessRequest, TeamRole } from "./lib/types";

const DESIRED: { key: AccessRequest["desired"]; label: string; blurb: string }[] = [
  { key: "bidder", label: "Bid a tender", blurb: "Receive documents and submit a sealed bid" },
  { key: "client", label: "Client portal", blurb: "Follow your tender as the employer" },
  { key: "project", label: "Project team", blurb: "Join a live project — site, consultant or client team" },
];

const TEAM_ROLES: { key: Exclude<TeamRole, "client">; blurb: string }[] = [
  { key: "admin", blurb: "Run the whole project — team, client portal, everything" },
  { key: "contractor", blurb: "Site delivery — reports, drawings, programme, materials" },
  { key: "consultant", blurb: "Review & respond — RFIs, observations, site visits" },
  { key: "technician", blurb: "Field work — daily reports, targets, photos, requests" },
  { key: "viewer", blurb: "Read-only across the project" },
];

export function RequestAccessModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { tenders } = useStore();
  const { projects } = usePmStore();
  const [desired, setDesired] = useState<AccessRequest["desired"]>("bidder");
  const [targetId, setTargetId] = useState("");
  const [role, setRole] = useState<Exclude<TeamRole, "client">>("consultant");
  const [showPerms, setShowPerms] = useState(false);
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  const targets = desired === "project"
    ? projects.map((p) => ({ id: p.id, label: `${p.name} (${p.code})` }))
    : tenders.map((t) => ({ id: t.id, label: `${t.name} (${t.code})` }));
  const target = targets.find((t) => t.id === targetId) ?? targets[0];

  function submit() {
    if (!name.trim() || !company.trim() || !email.trim() || !target) return;
    accessActions.submit({
      name: name.trim(), company: company.trim(), email: email.trim(),
      desired, targetKind: desired === "project" ? "project" : "tender",
      targetId: target.id, targetLabel: target.label,
      role: desired === "project" ? role : undefined,
      message: message.trim() || undefined,
    });
    setSent(true);
  }

  function close() {
    setSent(false); setName(""); setCompany(""); setEmail(""); setMessage("");
    onClose();
  }

  return (
    <Modal open={open} onClose={close} title="Request access" wide>
      {sent ? (
        <div style={{ textAlign: "center", padding: 18 }}>
          <Icon.checkCircle width={30} style={{ color: "var(--ok)" }} />
          <h3 className="serif" style={{ fontSize: 24, marginTop: 12 }}>Request sent</h3>
          <p className="muted" style={{ marginTop: 8, fontSize: 14, maxWidth: 400, marginLeft: "auto", marginRight: "auto" }}>
            The studio has been notified. Your access code arrives by email at <b>{email}</b> once approved.
          </p>
          <button className="btn" style={{ marginTop: 18 }} onClick={close}>Done</button>
        </div>
      ) : (
        <div className="col" style={{ gap: 14 }}>
          <div className="row wrap" style={{ gap: 8 }}>
            {DESIRED.map((d) => (
              <button key={d.key} className={"chip" + (desired === d.key ? " on" : "")} title={d.blurb} onClick={() => { setDesired(d.key); setTargetId(""); }}>{d.label}</button>
            ))}
          </div>
          <div className="field"><label>{desired === "project" ? "Project" : "Tender"}</label>
            <select className="select" value={target?.id ?? ""} onChange={(e) => setTargetId(e.target.value)}>
              {targets.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>

          {desired === "project" && (
            <div className="field">
              <label>Role you're requesting</label>
              <div className="col" style={{ gap: 8 }}>
                {TEAM_ROLES.map((r) => (
                  <button key={r.key} className="panel row" onClick={() => setRole(r.key)}
                    style={{ padding: "9px 13px", gap: 10, cursor: "pointer", textAlign: "left", borderColor: role === r.key ? "var(--accent)" : "var(--line)", background: role === r.key ? "color-mix(in srgb, var(--accent) 6%, var(--card))" : "var(--card)" }}>
                    <span style={{ width: 13, height: 13, borderRadius: 13, flexShrink: 0, border: "2px solid " + (role === r.key ? "var(--accent)" : "var(--line-2)"), background: role === r.key ? "var(--accent)" : "transparent" }} />
                    <span><span style={{ fontWeight: 600, fontSize: 13 }}>{LEVEL_LABEL[r.key]}</span><span className="dim" style={{ display: "block", fontSize: 11 }}>{r.blurb}</span></span>
                  </button>
                ))}
              </div>
              <button className="btn ghost sm" style={{ alignSelf: "flex-start", marginTop: 8, fontSize: 11.5 }} onClick={() => setShowPerms((s) => !s)}>
                <Icon.eye width={12} />{showPerms ? "Hide the permission table" : "See what each role can do"}
              </button>
              {showPerms && (
                <div style={{ overflow: "auto", marginTop: 8, border: "1px solid var(--line)", borderRadius: 12 }}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Capability</th>
                        {TEAM_ROLES.map((r) => <th key={r.key} style={{ textAlign: "center", whiteSpace: "nowrap", fontSize: 10 }}>{LEVEL_LABEL[r.key]}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {PERMISSIONS.map((p) => (
                        <tr key={p.label}>
                          <td style={{ fontSize: 12 }}>{p.label}</td>
                          {TEAM_ROLES.map((r) => (
                            <td key={r.key} style={{ textAlign: "center" }}>
                              {p.grants[r.key] ? <Icon.check width={13} style={{ color: "var(--ok)" }} /> : <span className="dim">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>The table is exactly what each role can do in the app — the studio confirms or adjusts your role at approval.</p>
            </div>
          )}
          <div className="row" style={{ gap: 12 }}>
            <div className="field" style={{ flex: 1 }}><label>Your name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="A. Persaud" /></div>
            <div className="field" style={{ flex: 1.2 }}><label>Company</label><input className="input" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Northline Construction Ltd." /></div>
          </div>
          <div className="field"><label>Email — your access code is sent here</label><input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tenders@northline.co" /></div>
          <div className="field"><label>Message (optional)</label><textarea className="textarea" style={{ minHeight: 70 }} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Who you are, why you need access…" /></div>
          <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
            <button className="btn ghost" onClick={close}>Cancel</button>
            <button className="btn accent" onClick={submit} disabled={!name.trim() || !company.trim() || !email.includes("@") || !target}>
              <Icon.send width={15} />Send request
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
